// src/components/RegisterForm.tsx

import React, { useState } from 'react';
import { TextField, Typography, useTheme, CircularProgress, InputAdornment, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PrimaryButton } from './';
import { TipoUsuario } from '../types/enums/TipoUsuario';
import TaxIdField, { cleanTaxId } from './TaxIdField';

interface RegisterFormProps {
  onRegister: (nome: string, email: string, senha: string, tipoUsuario: TipoUsuario, taxId: string, cellphone?: string) => Promise<{ needsPayment: boolean }>;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onRegister }) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [tipoUsuario, setTipoUsuario] = useState<TipoUsuario>(TipoUsuario.EMPRESA);
  const [taxId, setTaxId] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const theme = useTheme();



  const formatPhone = (value: string) => {
    const numbers = value.replaceAll(/\D/g, '');
    if (numbers.length <= 2) {
      return `+${numbers}`;
    } else if (numbers.length <= 4) {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2)}`;
    } else if (numbers.length <= 9) {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4)}`;
    } else {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9, 13)}`;
    }
  };

  const handleRegister = async () => {
    setError(null);
    setSuccess(null);

    if (!nome || nome.length < 3) {
      setError("Nome deve ter pelo menos 3 caracteres.");
      return;
    }

    if (!email?.includes('@')) {
      setError("Por favor, informe um email válido.");
      return;
    }

    if (!senha || senha.length < 6) {
      setError("Senha deve ter pelo menos 6 caracteres.");
      return;
    }

    const cleanTaxIdValue = cleanTaxId(taxId);
    if (!cleanTaxIdValue || (cleanTaxIdValue.length !== 11 && cleanTaxIdValue.length !== 14)) {
      setError("Por favor, informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.");
      return;
    }

    setLoading(true);
    
    try {
      const result = await onRegister(nome, email, senha, tipoUsuario, taxId, cellphone);
      
      if (result.needsPayment) {
        setSuccess("Cadastro realizado com sucesso! Redirecionando para pagamento...");
      } else {
        setSuccess("Cadastro realizado com sucesso!");
      }
      
      // Limpar campos após sucesso
      setNome('');
      setEmail('');
      setSenha('');
      setTaxId('');
      setCellphone('');
      setTipoUsuario(TipoUsuario.EMPRESA);
    } catch (err: any) {
      setError(err.message || "Ocorreu um erro ao criar a conta. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleRegister();
    }
  };

  return (
    <>
      {error && (
        <Typography
          sx={{
            color: theme.palette.error.contrastText,
            bgcolor: theme.palette.secondary.main,
            p: 1.5,
            borderRadius: '8px',
            width: '100%',
            mb: 2,
            fontSize: '0.875rem',
            textAlign: 'center'
          }}
        >
          {error}
        </Typography>
      )}

      {success && (
        <Typography
          sx={{
            color: theme.palette.success.dark,
            bgcolor: theme.palette.success.light,
            p: 1.5,
            borderRadius: '8px',
            width: '100%',
            mb: 2,
            fontSize: '0.875rem',
            textAlign: 'center'
          }}
        >
          {success}
        </Typography>
      )}

      <TextField
        label="Nome da empresa"
        variant="outlined"
        fullWidth
        color='secondary'
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        sx={{
          mb: 2,
          color: 'white',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: theme.palette.secondary.main,
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'white',
            opacity: 1
          }
        }}
      />

      <FormControl
        fullWidth
        color="secondary"
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: theme.palette.secondary.main,
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiSelect-icon': {
            color: 'white',
          }
        }}
      >
        <InputLabel>Você é?</InputLabel>
        <Select
          value={tipoUsuario}
          label="Tipo de usuário"
          onChange={(e) => setTipoUsuario(e.target.value as TipoUsuario)}
          sx={{
            color: 'white',
            '& .MuiSelect-select': {
              color: 'white',
            }
          }}
        >
          <MenuItem value={TipoUsuario.EMPRESA}>Empresa, preciso executar o planejamento da minha empresa</MenuItem>
          <MenuItem value={TipoUsuario.CONSULTOR}>Consultor, ajudo empresas no planejamento estratégico</MenuItem>
        </Select>
      </FormControl>

      <TaxIdField
        value={taxId}
        onChange={setTaxId}
        required
        sx={{
          mb: 2,
          color: 'white',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: theme.palette.secondary.main,
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'white',
            opacity: 1
          }
        }}
      />

      <TextField
        label="Telefone Celular (opcional)"
        variant="outlined"
        fullWidth
        color='secondary'
        value={cellphone}
        onChange={(e) => setCellphone(formatPhone(e.target.value))}
        placeholder="+55 (11) 99999-9999"
        slotProps={{ htmlInput: { maxLength: 19 } }}
        sx={{
          mb: 2,
          color: 'white',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: theme.palette.secondary.main,
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'white',
            opacity: 1
          }
        }}
      />

      <TextField
        label="Email"
        variant="outlined"
        fullWidth
        color='secondary'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{
          mb: 2,
          color: 'white',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: theme.palette.secondary.main,
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'white',
            opacity: 1
          }
        }}
      />

      <TextField
        label="Senha"
        variant="outlined"
        type={showPassword ? "text" : "password"}
        fullWidth
        color="secondary"
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: "white" }}
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ fontSize: 20, mr: 1 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 20, mr: 1 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }
        }}
        sx={{
          mb: 2,
          color: "white",
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            color: "white",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
          },
          "& .MuiInputLabel-root": {
            color: "rgba(255, 255, 255, 0.7)",
            "&.Mui-focused": {
              color: theme.palette.secondary.main,
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255, 255, 255, 0.3)",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "white",
            opacity: 1,
          },
        }}
      />

      <PrimaryButton
        variant="contained"
        color="secondary"
        fullWidth
        onClick={handleRegister}
        disabled={loading}
        sx={{ py: 1 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Criar conta"}
      </PrimaryButton>
    </>
  );
};

export default RegisterForm;